import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import { IChnlInteractiveThemeUI } from "lib/helper/channel-interactive-tool-theme";

/**
 * Centralized styles for IconBannerV2 and its sub-components
 */

// ============================================================================
// CHANNEL ICON STYLES
// ============================================================================
export const getChannelIconOuterBoxStyles = (theme?: IChnlInteractiveThemeUI, genreEnabled?: boolean, isClickable?: boolean) => ({
  backgroundColor: "transparent",
  borderRadius: "100%",
  position: "relative",
  width: {
    xs: "65px !important",
    sm: genreEnabled ? "78px !important" : "96px !important",
    md: "96px !important"
  },
  margin: {
    xs: "0px 0 0 0",
    sm: "8px 0 0 0",
    md: "0px 0 0 0"
  },
  padding: "0px",
  boxShadow: theme?.category?.mode === "light" ? "none" : `0px 2px 20px 0px rgba(0, 0, 0, 0.05)`,
  cursor: isClickable ? "pointer" : "auto"
});

export const getChannelIconInnerBoxStyles = (isClickable?: boolean) => ({
  backgroundColor: "transparent",
  borderRadius: "100%",
  position: "relative",
  width: {
    xs: "65px !important",
    sm: "96px !important"
  },
  margin: {
    xs: "12px 0 0 0",
    sm: "8px 0 0 0",
    md: "12px 0 0 0"
  },
  padding: "0px",
  boxShadow: `0px 2px 20px 0px rgba(0, 0, 0, 0.05)`,
  cursor: isClickable ? "pointer" : "auto",
  "&:hover": {
    backgroundColor: isClickable ? "#1FBCF87A !important" : "transparent"
  },
  "&:active": {
    backgroundColor: isClickable ? "#1FBCF87A !important" : "transparent"
  },
  "&:focus-visible": {
    outline: "2px solid #3885E1",
    outlineOffset: "-2px"
  },
  "&:inactive": {
    backgroundColor: "#1FBCF87A !important"
  }
});

export const channelIconMediaStyles = {
  paddingTop: "1.3px",
  borderRadius: "50%",
  border: "4px solid transparent",
  width: "100%",
  height: "100%"
};

export const channelIconTooltipPopperProps = {
  disablePortal: true,
  modifiers: [
    {
      name: "preventOverflow",
      enabled: true,
      phase: "main" as const
    },
    {
      name: "flip",
      enabled: true,
      phase: "main" as const
    },
    {
      name: "offset",
      options: {
        offset: [0, -20]
      }
    }
  ],
  sx: {
    "& .MuiTypography-root": {
      fontFamily: `roboto-regular`,
      fontSize: "14px"
    }
  }
};

export const channelIconTooltipStyles = {
  "& #popper[data-popper-escaped]": {
    visibility: "hidden"
  }
};

// ============================================================================
// CATEGORY SECTION STYLES
// ============================================================================
export const categorySectionContainerStyles = {
  display: "flex",
  flexDirection: "column",
  marginTop: "42px",
  justifyContent: "flex-start",
  maxWidth: "100%",
  minWidth: "100%"
};

export const getCategoryHeaderStyles = (theme?: IChnlInteractiveThemeUI) => ({
  ...(theme?.category?.mode === "light" && { background: theme?.channelHeaderBackground }),
  width: "100%",
  padding: "4px 20px",
  alignItems: "center",
  fontSize: "16px",
  color: theme?.category?.textColor || theme?.headerTextColor || colorPalette.neutral_white.main,
  lineHeight: {
    xs: "20.8px",
    md: "17.6px"
  },
  letterSpacing: { md: "0.48px" },
  fontWeight: {
    xs: 500,
    md: 700
  },
  paddingBottom: "4px",
  "& span": { marginLeft: "6px" }
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getCategoryTooltipStyles = (theme?: IChnlInteractiveThemeUI) => ({
  backgroundColor: `${colorPalette.white.main} !important`,
  color: `${colorPalette.jet.main} !important`
});

export const channelIconsContainerStyles = (showMoreKeys?: Record<string, boolean>, categoryKey?: string) => ({
  display: "flex",
  flexWrap: "wrap",
  marginLeft: "-5px",
  overflow: {
    xs: "hidden",
    sm: "visible"
  },
  width: "100%",
  maxHeight: {
    xs: showMoreKeys?.[categoryKey || ""] ? "5000px" : "270px",
    sm: "5000px"
  },
  transition: "max-height 300ms ease-in-out 100ms",
  justifyContent: {
    xs: "center",
    sm: "flex-start"
  },
  paddingBottom: { sm: "12px" }
});

// ============================================================================
// SHOW MORE BUTTON STYLES
// ============================================================================
export const showMoreButtonContainerStyles = {
  display: "block",
  visibility: "hidden",
  pointerEvents: {
    xs: "auto",
    sm: "none"
  },
  textAlign: "center",
  margin: "12px 0 12px",
  position: "relative"
};

export const getShowMoreButtonStyles = (theme?: IChnlInteractiveThemeUI) => ({
  padding: "4px 10px",
  fontSize: {
    xs: "14px",
    md: "16px"
  },
  fontWeight: 500,
  lineHeight: "18.2px",
  width: "120px",
  height: "36px",
  color: theme?.category?.mode === "light" ? colorPalette.jet.main : colorPalette.white.main,
  textDecoration: "none !important",
  transition: "all ease 200ms",
  "&:focus": {
    outline: "4px solid #1FBCF8",
    color: theme?.category?.mode === "light" ? colorPalette.jet.main : colorPalette.white.main
  },
  "&:hover": {
    textDecoration: "none !important",
    backgroundColor: theme?.category?.mode === "light" ? colorPalette.jet.main : "#FFF",
    color: theme?.category?.mode === "light" ? "#FFF" : colorPalette.jet.main,
    "& img": {
      filter:
        theme?.category?.mode === "light" ?
          "brightness(0) invert(1) !important" :
          "brightness(0) saturate(100%) invert(10%) sepia(68%) saturate(1229%) hue-rotate(186deg) brightness(95%) contrast(93%) !important"
    }
  },
  "&:active": {
    backgroundColor: theme?.category?.mode === "light" ? colorPalette.white.main : "#1FBCF83D",
    color: theme?.category?.mode === "light" ? "#1FBCF83D" : colorPalette.white.main,
    "& img": {
      filter:
        theme?.category?.mode === "light" ?
          "brightness(0) invert(0)" :
          "brightness(0) saturate(100%) invert(88%) sepia(100%) saturate(26%) hue-rotate(65deg) brightness(111%) contrast(106%)"
    }
  }
});

export const showMoreArrowIconStyles = (isExpanded?: boolean) => ({
  height: "16px",
  width: "16px",
  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
  transition: "transform 200ms ease-in-out"
});

// ============================================================================
// MY HOME TEAM CTA BUTTON STYLES
// ============================================================================
export const getMyHomeTeamCtaStyles = (theme?: IChnlInteractiveThemeUI) => ({
  mt: { xs: "10px" },
  marginBottom: {
    xs: "5.85px",
    sm: "0px"
  },
  padding: "5px 5px",
  textDecoration: "none !important",
  alignSelf: { xs: "flex-start" },
  marginLeft: "20px",
  color: theme?.category?.textColor || colorPalette.neutral_white.main,
  "&:hover": {
    textDecoration: "none !important"
  }
});

// ============================================================================
// ADD-ON CHANNELS SECTION STYLES
// ============================================================================
export const addonSectionContainerStyles = {
  display: "flex",
  justifyContent: "center",
  flexWrap: "wrap",
  padding: {
    xs: "24px 14px",
    sm: "11px 31px",
    md: "48px 50px"
  },
  minWidth: {
    xs: "calc(100vw - 123px)",
    sm: "calc(100vw - 275px)",
    md: "calc(100vw - 487px)",
    lg: "100%"
  },
  backgroundColor: "#21528C !important",
  order: 99,
  overflow: "hidden",
  maxWidth: "100vw",
  maxHeight: "100vh"
};

export const addonHeaderContainerStyles = {
  display: "flex",
  flexDirection: "column",
  width: "100%"
};

export const addonTitleStyles = {
  fontSize: {
    xs: "16px",
    md: "24px"
  },
  color: colorPalette.white.main,
  lineHeight: {
    xs: "20.8px",
    sm: "18.2px",
    md: "28.8px"
  },
  letterSpacing: {
    xs: "initial",
    md: "-0.48px"
  },
  fontWeight: 400,
  paddingBottom: {
    xs: "8px",
    sm: "10px",
    md: "12px"
  }
};

export const addonSubtitleStyles = {
  fontSize: {
    xs: "16px",
    sm: "14px",
    md: "20px"
  },
  color: colorPalette.white.main,
  lineHeight: {
    xs: "20.8px",
    sm: "18.2px",
    md: "26px"
  },
  fontWeight: {
    xs: 400,
    sm: 300,
    md: 400
  }
};

export const addonCategorySectionStyles = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  maxWidth: "100%",
  minWidth: "100%",
  paddingTop: {
    xs: "28px",
    sm: "16px",
    md: "40px"
  }
};

export const addonCategoryTitleStyles = {
  paddingBottom: "8px",
  fontSize: {
    xs: "16px",
    md: "20px"
  },
  color: colorPalette.white.main,
  lineHeight: {
    xs: "20.8px",
    md: "24px"
  },
  letterSpacing: { md: "-0.48px" },
  fontWeight: 500
};

export const addonChannelIconsContainerStyles = {
  display: "flex",
  flexWrap: "wrap",
  marginLeft: "-5px",
  overflowY: "hidden",
  width: "100%",
  maxHeight: "5000px",
  transition: "height 300ms ease-in-out 100ms",
  marginTop: {
    xs: "3px",
    sm: "1px"
  }
};

// ============================================================================
// MAIN CONTAINER STYLES
// ============================================================================
export const mainContainerStyles = {
  display: "flex",
  justifyContent: "center",
  flexWrap: "wrap",
  width: "100%"
};

export const zipcodeModuleContainerStyles = {
  padding: "10px 20px 0px 20px"
};
